import type { CommonData } from "../types/common";
import type {
  CollateralType,
  PcrApplicant,
  PcrCreditRegisterExtract,
  PcrIntegration,
  PcrLoan,
  PcrYearlyIncome
} from "../types/pcr";

// Pure data helpers for the Positive Credit Register (PCR) section. Kept free
// of React/styled imports so they can be unit-tested without the UI toolkit.

export const getPcr = (
  data: CommonData | undefined
): PcrIntegration | undefined => data?.integrations?.pcr;

export const getExtract = (
  applicant: PcrApplicant | undefined
): PcrCreditRegisterExtract | undefined =>
  applicant?.response?.creditRegisterExtract;

// Live data nests income inside the extract; the backend type keeps it a
// sibling of the extract. Read whichever is present.
export const getIncomeData = (
  applicant: PcrApplicant | undefined
): PcrYearlyIncome[] =>
  getExtract(applicant)?.incomeData ?? applicant?.response?.incomeData ?? [];

// True when any applicant has a PCR extract to show.
export const pcrHasData = (data: CommonData | undefined): boolean => {
  const pcr = getPcr(data);
  return Boolean(getExtract(pcr?.applicant) || getExtract(pcr?.coapplicant));
};

export const loanBalance = (loan: PcrLoan): number | undefined =>
  loan.lumpSumLoan?.balance ?? loan.runningAccountLoan?.balance;

export interface CurrencyTotal {
  currencyCode: string;
  sum: number;
}

// Outstanding balance across loans, split by whether they are secured and
// grouped per currency. Balances are never summed across currencies — PCR is
// EUR in practice, but an extract could carry several, and adding them would
// produce a meaningless total. Ordered by descending sum.
//
// Loans whose `isLoanWithCollateral` flag is absent are excluded from both
// buckets rather than defaulted to unsecured, so an unknown status never
// inflates a total.
export const sumBalanceByCollateral = (
  loans: PcrLoan[],
  withCollateral: boolean
): CurrencyTotal[] => {
  const totals = new Map<string, number>();
  for (const loan of loans) {
    if (loan.isLoanWithCollateral !== withCollateral) {
      continue;
    }
    const balance = loanBalance(loan);
    if (balance === undefined) {
      continue;
    }
    const currencyCode = loan.currencyCode || "EUR";
    totals.set(currencyCode, (totals.get(currencyCode) ?? 0) + balance);
  }
  return [...totals.entries()]
    .map(([currencyCode, sum]) => ({ currencyCode, sum }))
    .sort((a, b) => b.sum - a.sum);
};

// Collateral types for a loan, normalized to an array. The live data uses the
// plural `collateralTypes`; the backend type also allows a singular
// `collateralType` (scalar or array), so handle every shape.
export const getCollateralTypes = (loan: PcrLoan): CollateralType[] => {
  const raw = loan.collateralTypes ?? loan.collateralType;
  if (!raw) {
    return [];
  }
  return Array.isArray(raw) ? raw : [raw];
};

// "LoanForPurchaseOfVehicleOrCraft" -> "Loan For Purchase Of Vehicle Or Craft".
export const humanizePcrEnum = (value: string | undefined): string => {
  if (!value) {
    return "-";
  }
  return value.replace(/([a-z])([A-Z])/g, "$1 $2");
};
